import { Component, inject, OnInit, Renderer2 } from '@angular/core';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { changeLanguageAction, toastAction } from './@shared/store/common.actions';
import { debounceTime, filter } from 'rxjs/operators';
import * as _ from 'lodash';
import { DOCUMENT, Location, NgIf, NgClass } from '@angular/common';
import { setInitUrl } from './@shared/store/router/router.actions';
import { ConfigService } from '@gridscale/ingrid/helper/services/config.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { MessageService, PrimeNGConfig, SharedModule } from '@js/primeng17/api';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { getCurrentUser } from './@shared/store/user/user.selectors';
import { HotkeysService } from './@shared/services/hotkeys.service';
import { environment } from '../environments/environment';
import { CopyClipboardModule } from '@gridscale/ingrid/components/copy-clipboard';
import { A11yStatusComponent } from '@gridscale/ingrid/components/a11ystatus';
import { ToastModule } from '@js/primeng17/toast';
import { A11yLinkDirective } from '@gridscale/ingrid/helper/directives';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [MessageService],
    imports: [
        A11yLinkDirective,
        A11yStatusComponent,
        CopyClipboardModule,
        NgClass,
        NgIf,
        RouterOutlet,
        SharedModule,
        ToastModule,
        TranslateModule,
    ]
})
export class AppComponent implements OnInit {

  readonly #actions$ = inject(ActionsSubject);
  readonly #store = inject(Store);
  readonly #location = inject(Location);
  readonly #translate = inject(TranslateService);
  readonly #titleService = inject(Title);
  readonly #messageService = inject(MessageService);
  readonly #router = inject(Router);
  readonly #hkService = inject(HotkeysService);
  readonly #primeConfig = inject(PrimeNGConfig);
  readonly #document = inject(DOCUMENT);
  readonly #renderer = inject(Renderer2);
  readonly #config = inject(ConfigService);




  ngOnInit() {
    if ((this.#config.isLocalhost ? environment.apiUrl : window.location.hostname).indexOf('gridscale') === -1) {
      this.#renderer.addClass(this.#document.body, 'brand');
    }



    this.#hkService.noop(); // just to have it initialized because of debug center

    // set the page title from translations
    this.#translate.stream('GENERAL.PAGE_TITLE', { name: this.#config?.partnerName }).subscribe(title => {
      this.#titleService.setTitle(title);
    });

    // show toast messages dispatched via ngrx actions
    this.#actions$.pipe(ofType(toastAction)).subscribe(message => {
      this.#messageService.add({
        key: 'global',
        ...message
      });
    });

    this.#store.dispatch(setInitUrl({ url: this.#location.path() }));

    // when current user is loaded, set the language
    this.#store
      .select(getCurrentUser)
      .pipe(filter(user => user?.language !== undefined))
      .subscribe(user => {
        this.#translate.use(user!.language!);
      });

    this.#actions$.pipe(
      ofType(changeLanguageAction),
      debounceTime(100)
    ).subscribe(action => {
      this.#translate.use(action.language);
    });




    // override the route reuse strategy:
    // if we have route params which contain "_uuid" and they change between two routes, we do *not* reuse the route,
    // meaning that the component will be completely destroyed and recreated in this case. Otherwise it will lead to problems
    // e.g. when switching object via the nav-buttons, as we often use ngOnInit() to initialize our stuff
    this.#router.routeReuseStrategy.shouldReuseRoute = (curr, future) => {
      const curKeys = curr.params ? Object.keys(curr.params) : [];
      const uuidKeys = _.filter(curKeys, (o: string) => o.indexOf('_uuid') > 0);

      let uuidChanged = false;
      _.forEach(uuidKeys, key => {
        if (future.params[key] !== undefined && curr.params[key] !== future.params[key]) {
          // uuid changed
          uuidChanged = true;
        }
      });

      if (uuidChanged) {
        return false; // do not reuse
      }
      return future.routeConfig === curr.routeConfig;
    };

    this.#translate.get('PRIMENG').subscribe(res => this.#primeConfig.setTranslation(res));

    this.#translate.onLangChange.subscribe(() => this.#translate.get('PRIMENG').subscribe(res => this.#primeConfig.setTranslation(res)));

    // GD-15347: A11y: After routing we always focus the main header element to not confuse sightless people
    this.#router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      debounceTime(100)
    ).subscribe(() => {
      document.querySelector('header')?.focus();
    })
  }



}
